import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../common/Card';
import Input from '../common/Input';
import Button from '../common/Button';
import Alert from '../common/Alert';
import api from '../../services/api';
import { validateEmail, validatePhone } from '../../utils/validators';
import '../../styles/customer-form.css';

const CustomerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    id_number: '',
    address: '',
    credit_limit: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (isEditMode) {
      loadCustomer();
    }
  }, [id]);

  const loadCustomer = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/customers.php/${id}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Failed to load customer:', error);
      setAlert({
        type: 'error',
        message: 'Failed to load customer details'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Invalid phone number';
    }

    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (formData.credit_limit && (isNaN(formData.credit_limit) || formData.credit_limit < 0)) {
      newErrors.credit_limit = 'Invalid credit limit';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        await api.put(`/customers.php/${id}`, formData);
        setAlert({
          type: 'success',
          message: 'Customer updated successfully'
        });
      } else {
        const response = await api.post('/customers.php', formData);
        setAlert({
          type: 'success',
          message: 'Customer created successfully'
        });
        setTimeout(() => {
          navigate(`/customers/${response.data.customer_id}`);
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to save customer:', error);
      setAlert({
        type: 'error',
        message: error.response?.data?.error || 'Failed to save customer'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="customer-form">
      <Card>
        <div className="form-header">
          <h2>{isEditMode ? 'Edit Customer' : 'Add New Customer'}</h2>
          <Button variant="secondary" onClick={() => navigate('/customers')}>
            Cancel
          </Button>
        </div>

        {alert && (
          <Alert
            type={alert.type}
            message={alert.message}
            closable
            onClose={() => setAlert(null)}
          />
        )}

        <form onSubmit={handleSubmit} className="form">
          <div className="form-row">
            <div className="form-col-6">
              <Input
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                error={errors.first_name}
                required
              />
            </div>
            <div className="form-col-6">
              <Input
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                error={errors.last_name}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-col-6">
              <Input
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={errors.phone}
                placeholder="0712345678"
                required
              />
            </div>
            <div className="form-col-6">
              <Input
                label="Email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                placeholder="customer@example.com"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-col-6">
              <Input
                label="ID Number"
                name="id_number"
                value={formData.id_number}
                onChange={handleChange}
                placeholder="National ID/Passport"
              />
            </div>
            <div className="form-col-6">
              <Input
                label="Credit Limit"
                name="credit_limit"
                type="number"
                value={formData.credit_limit}
                onChange={handleChange}
                error={errors.credit_limit}
                prefix="KSh"
                hint="Maximum credit allowed"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-col-12">
              <Input
                label="Address"
                name="address"
                type="textarea"
                value={formData.address}
                onChange={handleChange}
                rows="3"
                placeholder="Physical address"
              />
            </div>
          </div>

          <div className="form-actions">
            <Button type="submit" variant="primary" loading={loading}>
              {isEditMode ? 'Update Customer' : 'Create Customer'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CustomerForm;